/**
 * 정부부처/전문기관별 보기 공통 팝업
 * - 좌측: 정부부처 체크리스트, 우측: 전문기관 체크리스트
 * - 확인 시 onClose(true, { selectedMinistries, selectedAgencies }) 호출
 *
 * config.title - 팝업 제목 (기본: 정부부처/전문기관별 보기)
 * config.initialSelectedMinistries - string[] 초기 선택 정부부처
 * config.initialSelectedAgencies - string[] 초기 선택 전문기관
 * config.govMinistries - string[] 정부부처 목록 (미제공 시 기본값 사용)
 * config.specializedAgencies - string[] 전문기관 목록 (미제공 시 기본값 사용)
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';

// 기본 정부부처 목록 (캡처 기준)
export const DEFAULT_GOV_MINISTRIES = [
  '전체', '범부처', '다부처', '기획재정부', '과학기술정보통신부', '교육부', '법무부', '국방부', '행정안전부',
  '문화체육관광부', '농림축산식품부', '산업통상부', '보건복지부', '기후에너지환경부', '해양수산부',
  '산림청', '우주항공청', '질병관리청'
];

// 기본 전문기관 목록 (캡처 기준)
export const DEFAULT_SPECIALIZED_AGENCIES = [
  '한국연구재단', '중소기업기술정보진흥원', '한국보건산업진흥원', '한국산업기술기획평가원',
  '정보통신기획평가원', '한국과학기술기획평가원', '국토교통과학기술진흥원', '한국산업기술진흥원',
  '한국에너지기술평가원', '농촌진흥청', '해양수산과학기술진흥원', '농림식품기술기획평가원',
  '한국환경산업기술원', '한국임업진흥원', '우주항공청'
];

const DeptAgencySelectPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const title = config?.title ?? t('popup.deptAgencySelectPopup.title');
  const govMinistries = useMemo(
    () => (Array.isArray(config?.govMinistries) && config.govMinistries.length > 0
      ? config.govMinistries
      : DEFAULT_GOV_MINISTRIES),
    [config?.govMinistries]
  );
  const specializedAgencies = useMemo(
    () => (Array.isArray(config?.specializedAgencies) && config.specializedAgencies.length > 0
      ? config.specializedAgencies
      : DEFAULT_SPECIALIZED_AGENCIES),
    [config?.specializedAgencies]
  );

  const initialMinistries = useMemo(
    () => new Set(Array.isArray(config?.initialSelectedMinistries) ? config.initialSelectedMinistries : []),
    [config?.initialSelectedMinistries]
  );
  const initialAgencies = useMemo(
    () => new Set(Array.isArray(config?.initialSelectedAgencies) ? config.initialSelectedAgencies : []),
    [config?.initialSelectedAgencies]
  );

  const [tempMinistries, setTempMinistries] = useState(() => new Set(initialMinistries));
  const [tempAgencies, setTempAgencies] = useState(() => new Set(initialAgencies));

  const toggleMinistry = useCallback((name) => {
    if (name === '전체') {
      setTempMinistries((prev) => {
        const isCurrentlyAllChecked = prev.has('전체');
        if (isCurrentlyAllChecked) return new Set();
        return new Set(govMinistries);
      });
      return;
    }
    setTempMinistries((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, [govMinistries]);

  const toggleAgency = useCallback((name) => {
    setTempAgencies((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    onClose(true, {
      selectedMinistries: Array.from(tempMinistries),
      selectedAgencies: Array.from(tempAgencies)
    });
  }, [tempMinistries, tempAgencies, onClose]);

  return (
    <PopupWrapper
      config={{ ...config, title }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-dept-agency-select"
    >
      <div className="dept-agency-popup-body">
        <div className="dept-agency-popup-col">
          <div className="dept-agency-popup-col-title">{t('popup.deptAgencySelectPopup.governmentMinistry')}</div>
          <div className="dept-agency-popup-list">
            {govMinistries.map((name) => (
              <label key={name} className="dept-agency-popup-check">
                <input
                  type="checkbox"
                  checked={tempMinistries.has(name)}
                  onChange={() => toggleMinistry(name)}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="dept-agency-popup-sep" aria-hidden="true">&gt;</div>
        <div className="dept-agency-popup-col">
          <div className="dept-agency-popup-col-title">{t('popup.deptAgencySelectPopup.specializedAgency')}</div>
          <div className="dept-agency-popup-list">
            {specializedAgencies.map((name) => (
              <label key={name} className="dept-agency-popup-check">
                <input
                  type="checkbox"
                  checked={tempAgencies.has(name)}
                  onChange={() => toggleAgency(name)}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="dept-agency-popup-footer">
        <button type="button" className="dept-agency-popup-confirm" onClick={handleConfirm}>
          {t('popup.common.confirm')}
        </button>
      </div>

      <style>{`
        .popup-dept-agency-select .dept-agency-popup-body {
          display: grid;
          grid-template-columns: minmax(220px, 1fr) auto minmax(220px, 1fr);
          gap: 0;
          padding: 0px;
          min-height: 360px;
          overflow: hidden;
        }
        .popup-dept-agency-select .dept-agency-popup-col {
          display: flex;
          flex-direction: column;
          min-width: 0;
          overflow: hidden;
        }
        .popup-dept-agency-select .dept-agency-popup-col-title {
          font-size: 15px;
          font-weight: 700;
          color: #1e3a5f;
          margin-bottom: 12px;
          flex-shrink: 0;
        }
        .popup-dept-agency-select .dept-agency-popup-list {
          overflow-y: auto;
          overflow-x: hidden;
          height: 400px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          padding: 10px 12px;
          background: #fff;
        }
        .popup-dept-agency-select .dept-agency-popup-check {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 6px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
        }
        .popup-dept-agency-select .dept-agency-popup-check:hover {
          background: #f3f4f6;
          border-radius: 4px;
        }
        .popup-dept-agency-select .dept-agency-popup-check input[type="checkbox"] {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          margin: 0;
          cursor: pointer;
        }
        .popup-dept-agency-select .dept-agency-popup-check span {
          flex-shrink: 0;
          word-break: keep-all;
          line-height: 1.5;
        }
        .popup-dept-agency-select .dept-agency-popup-sep {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
          font-size: 20px;
          font-weight: 500;
          padding: 0 16px;
        }
        .popup-dept-agency-select .dept-agency-popup-footer {
          padding: 18px 24px;
          border-top: 1px solid #e5e7eb;
          display: flex;
          justify-content: center;
        }
        .popup-dept-agency-select .dept-agency-popup-confirm {
          min-width: 140px;
          padding: 12px 28px;
          background: #1e40af;
          border: none;
          border-radius: 6px;
          color: #fff;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
        }
        .popup-dept-agency-select .dept-agency-popup-confirm:hover {
          background: #1d4ed8;
        }
      `}</style>
    </PopupWrapper>
  );
};

export default DeptAgencySelectPopup;
